import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { hasTokenSelector } from '@axilon-apps/domains-identity/state'
import { parseFlagSnapshot, snapshotsEqual, type FlagSnapshot } from '@atbash-labs/ontology-flags'
import { useAppSelector, useGetFlagSnapshotQuery } from '@axilon-apps/domains-ontology-ui-state'

const BOOTSTRAP_TIMEOUT_MS = 2_000
const POLL_MS = 60_000
const EMPTY_SNAPSHOT: FlagSnapshot = parseFlagSnapshot(null)

interface FlagSnapshotContextValue {
  snapshot: FlagSnapshot
  stale: boolean
}

const FlagSnapshotContext = React.createContext<FlagSnapshotContextValue>({
  snapshot: EMPTY_SNAPSHOT,
  stale: false
})

export function useFlagSnapshot(): FlagSnapshotContextValue {
  return React.useContext(FlagSnapshotContext)
}

/**
 * Fetches the proxy snapshot once at bootstrap (short timeout → generated
 * defaults), then pins it for the session. Background polls mark stale and
 * apply atomically on the next route navigation.
 */
export function FlagSnapshotProvider({
  children
}: {
  children: React.ReactNode
}): React.JSX.Element {
  const hasToken = useAppSelector(hasTokenSelector)
  const location = useLocation()
  const { data } = useGetFlagSnapshotQuery(undefined, {
    skip: !hasToken,
    pollingInterval: hasToken ? POLL_MS : 0
  })

  const [pinned, setPinned] = React.useState<FlagSnapshot>(EMPTY_SNAPSHOT)
  const [pending, setPending] = React.useState<FlagSnapshot | null>(null)
  const [stale, setStale] = React.useState(false)
  const [ready, setReady] = React.useState(!hasToken)
  const appliedInitial = React.useRef(false)
  const pathRef = React.useRef(location.pathname)

  React.useEffect(() => {
    if (ready) return
    const timer = window.setTimeout(() => setReady(true), BOOTSTRAP_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [ready])

  React.useEffect(() => {
    if (!data) return
    if (!appliedInitial.current) {
      appliedInitial.current = true
      setPinned(data)
      setReady(true)
      return
    }
    if (!snapshotsEqual(data.flags, pinned.flags)) {
      setPending(data)
      setStale(true)
    }
  }, [data, pinned.flags])

  React.useEffect(() => {
    if (location.pathname === pathRef.current) return
    pathRef.current = location.pathname
    if (pending) {
      setPinned(pending)
      setPending(null)
      setStale(false)
    }
  }, [location.pathname, pending])

  const value = React.useMemo(() => ({ snapshot: pinned, stale }), [pinned, stale])

  if (!ready) return <></>

  return <FlagSnapshotContext.Provider value={value}>{children}</FlagSnapshotContext.Provider>
}
